import dayjs from 'dayjs'
import { Hall } from '@weblite/ts-action/hall'
import { Timestamp } from '@weblite/ts-action'
import { retryPromiseUntilSucceed } from '@weblite/logic-toolkit'
import {
  showFailedToStartTrialSnackbar,
  showTrialCantBeActivatedBecauseOfActivatedPlansErrorSnackbar,
  showTrialIsActivatedOnceErrorSnackbar,
  showTrialStartedSuccessfullySnackbar,
} from '$comp/snackbars'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import { getClientUser } from '$logic/client-user'
import { getAllHall, getHall } from '$logic/hall'
import {
  PREMIUM_PLAN_TAG,
  TEACHER_PLAYGROUND_CHANNEL_TAG,
} from '$logic/hall/constant'
import { createTeacherPlaygroundChannel } from '$logic/profile'
import { decodeSubscriptionPermissions } from '$logic/subscription'
import { getUserDisplayName } from '$logic/users'
import { createTrial, getAllTrials, getPlannerStats } from './http-requests'
import { DeferredPromise } from '$helper/functions/deferred-promise'
import { connectionStatusView } from '$comp/main/root.reducer'
import { packagesStatsStorage } from './storage'
import { PackageType, ResGetStats } from './types'
import { getPackageProgressPercentage } from './packages.helpers'
import { showEnablePremiumFeaturesTrialConfirmModal } from '$comp/modals/confirmation-dialog'
import { IS_IN_ALPHA_SCHOOL } from '$setup/config/env'

const retryDelays = [1000, 3000, 6000, 10000]

export const TRIAL_PERIOD = 7

const syncPackagesStatsPromise = new DeferredPromise<void>()

export async function waitForPackagesStatsToSync() {
  return syncPackagesStatsPromise.wait()
}

export async function syncPackagesStats() {
  if (IS_IN_ALPHA_SCHOOL) return

  const fetchTrials = retryPromiseUntilSucceed(
    async () => {
      return await getAllTrials()
    },
    retryCount => {
      if (connectionStatusView() !== 'connected') return 2000
      return retryDelays[Math.min(retryCount - 1, retryDelays.length - 1)]
    },
  )

  const fetchPlannerStats = retryPromiseUntilSucceed(
    async () => {
      return await getPlannerStats()
    },
    retryCount => {
      if (connectionStatusView() !== 'connected') return 2000
      return retryDelays[Math.min(retryCount - 1, retryDelays.length - 1)]
    },
  )

  const plannerStats = await fetchPlannerStats()

  const hasAnyPackages = Object.keys(plannerStats).some(planType => {
    const plan = plannerStats[planType as keyof ResGetStats]
    return !!plan.current || !!plan.last || plan.reserves?.length > 0 // helper function
  })

  const endingPackagesCount = Object.keys(plannerStats).reduce(
    (count, planType) => {
      const plan = plannerStats[planType as keyof ResGetStats]

      const hasReserves = plan.reserves?.length > 0
      if (hasReserves || !plan.current) return count

      const currentPackageProgress = getPackageProgressPercentage(
        planType as PackageType,
        plan.current,
        false,
      )

      return currentPackageProgress >= 90 ? count + 1 : count
    },
    0,
  )

  const trial = await fetchTrials()
  const trialCreatedAt =
    trial.onlineClass?.createdAt ||
    trial.recording?.createdAt ||
    trial.vod?.createdAt

  if (hasAnyPackages || trialCreatedAt) {
    packagesStatsStorage.set({
      ...(hasAnyPackages && { hasAnyPackages: true, endingPackagesCount }),
      ...(trialCreatedAt && {
        trialExpiresAt: dayjs(new Date(trialCreatedAt))
          .add(TRIAL_PERIOD, 'days')
          .valueOf(),
      }),
    })
  }

  return syncPackagesStatsPromise.resolve()
}

export async function getTrialEndTime(): Promise<Timestamp> {
  await waitForPackagesStatsToSync()
  const trial = packagesStatsStorage.get()
  return trial?.trialExpiresAt ?? 0
}

export async function hasTrialBeenUsed() {
  await waitForPackagesStatsToSync()
  const trial = packagesStatsStorage.get()
  return !!trial?.trialExpiresAt
}

function getTeacherPlaygroundChannelId() {
  const halls = getAllHall()
  const hall = halls.find(h => h.tags?.includes(TEACHER_PLAYGROUND_CHANNEL_TAG))
  return hall?.roomId
}

export const isTeacherPlaygroundChannel = (tags: string[] | undefined) =>
  tags?.includes(TEACHER_PLAYGROUND_CHANNEL_TAG)

export const hasPremiumPlanTag = (tags: string[] | undefined) =>
  Boolean(tags?.includes(PREMIUM_PLAN_TAG))

export const isPremiumPlanEnabled = async (hall: Hall) => {
  if (IS_IN_ALPHA_SCHOOL) return true
  const tags = hall.tags
  return hasPremiumPlanTag(tags)
}

export const shouldShowPremiumTrialModal = async (hallId: string) => {
  if (IS_IN_ALPHA_SCHOOL) return false

  const isAnyPlanEnabled = checkIsAnyPlanEnabled()
  if (isAnyPlanEnabled) return false

  const hall = await getHall(hallId)
  const isPlaygroundChannel = isTeacherPlaygroundChannel(hall.tags)
  const { isAdmin } = await decodeSubscriptionPermissions(hallId)
  const usedTrial = await hasTrialBeenUsed()
  return isPlaygroundChannel && isAdmin && !usedTrial
}

export async function enablePremiumFeaturesTrial() {
  if (IS_IN_ALPHA_SCHOOL) return
  try {
    const teacherPlaygroundChannelId = getTeacherPlaygroundChannelId()
    if (!teacherPlaygroundChannelId) {
      const clientUser = getClientUser()
      await createTeacherPlaygroundChannel(getUserDisplayName(clientUser))
    }

    await createTrial()

    packagesStatsStorage.set({
      trialExpiresAt: dayjs(new Date(dateWithOffset()))
        .add(TRIAL_PERIOD, 'day')
        .valueOf(),
    })

    analite('PREMIUM_FEATURES_TRIAL_STARTED')
    showTrialStartedSuccessfullySnackbar()
  } catch (e: any) {
    showFailedToStartTrialSnackbar()
    analite('FAILED_TO_START_TRIAL', {
      error: `${e.message}`,
    })
  }
}

const checkIsAnyPlanEnabled = () => {
  const trial = packagesStatsStorage.get()
  return !!trial?.hasAnyPackages
}

export async function checkTrialCanBeActivated() {
  if (IS_IN_ALPHA_SCHOOL) return

  const trialHasBeenUsed = await hasTrialBeenUsed()
  if (trialHasBeenUsed) return showTrialIsActivatedOnceErrorSnackbar()
  const isAnyPlanEnabled = checkIsAnyPlanEnabled()
  if (isAnyPlanEnabled)
    return showTrialCantBeActivatedBecauseOfActivatedPlansErrorSnackbar()
  void showEnablePremiumFeaturesTrialConfirmModal()
  analite('SHOW_ENABLE_TRIAL_DEMO_CONFIRM_MODAL_WITH_START_LINK')
}
